import EnvConfigUtil from './EnvConfigUtil';
import { EnvironmentConfig } from './types';

export const getEnvConfigByKey = (key: string): string | null => {
  return EnvConfigUtil.getEnvConfigByKey(ENV_TO_CONFIGS, key);
};

export const getDomainUrl = (region: string): string => {
  let key = EnvConfigUtil.getEnvKey();

  if (!key || key === 'beta_na') {
    return window.location.origin;
  }

  let stage = key.split('_')[0] + '_' + region;

  return ENV_TO_CONFIGS[stage]['url'];
};

export const getEnvConfigs = () => {
  return EnvConfigUtil.getEnvConfigs(ENV_TO_CONFIGS);
};

const DEFAULT_RUM_CONFIG = {
  sessionEventLimit: 0, // ensures no limit on events from a session
  allowCookies: true,
  enableXRay: false,
  signing: true,
  enableRumClient: true,
  disableAutoPageView: true, // handled by useEffect listener
  releaseId: process.env.REACT_APP_VERSION,
  telemetries: [
    [
      'errors',
      {
        stackTraceLength: 10000,
      },
    ],
    'performance',
    'http',
  ],
};

// NOTE on katalLoggerUrl (CS-Koala-3920, Oct 2026):
// The Katal Logger backend (API Gateway + Lambda + CloudWatch) that powered
// client-side log ingestion was decommissioned. The hardcoded endpoint
// (xqckjb302i.execute-api.us-west-2.amazonaws.com) has been returning
// NXDOMAIN since at least March 2026, generating ~1.64M failed DNS lookups
// per week across CSA devices.
//
// katalLoggerUrl is now set to '' in all environments. With an empty URL,
// KatalLogger silently skips HTTP POSTs and only logs to the browser console
// (controlled by logToConsole in Logger.ts). The logger.info/error/warn calls
// throughout the codebase continue to work for console output and CloudWatch
// RUM (error-level events are forwarded to RUM via the RumLogger wrapper).
//
// Current client-side observability alternatives:
//   - CloudWatch RUM: active on beta_na, gamma (NA/EU/FE), prod (NA/EU/FE).
//     App monitors are in account 753508751193 (stack in PerformancePortalAgnosticCDK).
//     Use CloudWatch Logs Insights on the RUMService log group to query JS errors,
//     HTTP failures, and performance data.
//   - Browser console: logger output is visible in DevTools (logToConsole: true).
//
// To revive Katal Logger backend logging:
//   1. Add KatalLoggerCloudWatchBackend = 2.0 to PerformancePortalAgnosticCDK Config
//   2. Add '@amzn/katal-logger-cdk' to PerformancePortalAgnosticCDK package.json
//   3. Instantiate the KatalLogger CDK construct in pp-agnostic-web-stack.ts
//   4. Wire the generated API Gateway URL via deploy-time replacement ({{KatalLoggerUrl}})
//   5. Replace the empty katalLoggerUrl values below with '{{KatalLoggerUrl}}'
//   See: https://code.amazon.com/packages/KatalLoggerCloudWatchBackend/blobs/mainline/--/README.md
const ENV_TO_CONFIGS: { [env: string]: EnvironmentConfig } = {
  sandbox: {
    graphQLEndpoint:
      'https://2ew2h4r7ofakbkosnztavyyn24.appsync-api.us-west-2.amazonaws.com/graphql', // Replace this GraphQL Endpoint with your own stack one
    region: 'us-west-2',
    featureConfigUrl: 'https://sandbox.feature-config.inspire.cs.amazon.dev',
    sushiUrl: 'https://unagi-na.amazon.com/1/events/com.amazon.csm.csa.prod',
    cscEntityPrefix: 'cscentral_na_user',
    katalLoggerUrl: '', // disabled; see NOTE above
    cscDomain: 'https://cscentral-na.integ.amazon.com',
    regionName: 'na',
    url: 'https://beta.inspire.cs.amazon.dev',
  },
  beta_internal_na: {
    graphQLEndpoint:
      'https://ovv54iwzmzdpze6d4ks7sk63iu.appsync-api.us-west-2.amazonaws.com/graphql',
    notificationGraphqlEndpoint:
      'https://25pszsuqgvbhzetytdnjefu44y.appsync-api.us-east-1.amazonaws.com/graphql',
    notificationRegion: 'us-east-1',
    notificationSTSRoleArn:
      'arn:aws:iam::311123381819:role/AppSyncClientRole-inspire',
    region: 'us-west-2',
    featureConfigUrl: 'https://beta-fe.inspire-feature-config.cs.amazon.dev',
    sushiUrl: 'https://unagi-na.amazon.com/1/events/com.amazon.csm.csa.prod',
    cscEntityPrefix: 'cscentral_na_user',
    katalLoggerUrl: '', // disabled; see NOTE above
    cscDomain: 'https://cscentral-na.integ.amazon.com',
    regionName: 'na',
    url: 'https://beta.inspire.cs.amazon.dev',
  },
  beta_na: {
    graphQLEndpoint: 'https://beta.appsync.inspire.cs.amazon.dev/graphql',
    notificationGraphqlEndpoint:
      'https://5g4iewgdszhihmk2lkm4eoq36a.appsync-api.us-east-1.amazonaws.com/graphql',
    notificationRegion: 'us-east-1',
    notificationSTSRoleArn:
      'arn:aws:iam::896148724331:role/AppSyncClientRole-inspire',
    region: 'us-west-2',
    featureConfigUrl: 'https://beta-fe.inspire-feature-config.cs.amazon.dev',
    sushiUrl: 'https://unagi-na.amazon.com/1/events/com.amazon.csm.csa.prod',
    cscEntityPrefix: 'cscentral_na_user',
    katalLoggerUrl: '', // disabled; see NOTE above
    cscDomain: 'https://cscentral-na.integ.amazon.com',
    regionName: 'na',
    url: 'https://beta.inspire.cs.amazon.dev',
    rumConfig: {
      appConfig: {
        APPLICATION_ID: 'e0403a07-4a8d-453c-bb2a-6d34d00f9410',
        APPLICATION_VERSION: '1.0.0',
        APPLICATION_REGION: 'us-east-1',
      },
      runConfig: {
        ...DEFAULT_RUM_CONFIG,
        identityPoolId: 'us-east-1:6e38bdc2-7c8e-48e1-91c2-a0f8bb8e1163',
        endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
      },
    },
  },
  gamma_na: {
    graphQLEndpoint: 'https://na-gamma.appsync.inspire.cs.amazon.dev/graphql',
    notificationGraphqlEndpoint:
      'https://gwtr6mnlwfbazpw7lqaruuweoq.appsync-api.us-east-1.amazonaws.com/graphql',
    notificationRegion: 'us-east-1',
    notificationSTSRoleArn:
      'arn:aws:iam::262366952739:role/AppSyncClientRole-inspire',
    region: 'us-east-1',
    featureConfigUrl: 'https://gamma-na.inspire-feature-config.cs.amazon.dev',
    sushiUrl: 'https://unagi-na.amazon.com/1/events/com.amazon.csm.csa.prod',
    cscEntityPrefix: 'cscentral_na_user',
    katalLoggerUrl: '', // disabled; see NOTE above
    cscDomain: 'https://cscentral.amazon.com',
    regionName: 'na',
    url: 'https://na-gamma.inspire.cs.amazon.dev',
    rumConfig: {
      appConfig: {
        APPLICATION_ID: '5689264e-3d45-479c-a1bd-752cc9b21e75',
        APPLICATION_VERSION: '1.0.0',
        APPLICATION_REGION: 'us-east-1',
      },
      runConfig: {
        ...DEFAULT_RUM_CONFIG,
        identityPoolId: 'us-east-1:8c713e60-ffc7-4836-b739-cd31d4263687',
        endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
      },
    },
  },
  gamma_eu: {
    graphQLEndpoint: 'https://eu-gamma.appsync.inspire.cs.amazon.dev/graphql',
    notificationGraphqlEndpoint:
      'https://eknlwnehwbaq5mtggvgj7cadjy.appsync-api.eu-west-1.amazonaws.com/graphql',
    notificationRegion: 'eu-west-1',
    notificationSTSRoleArn:
      'arn:aws:iam::262366952739:role/AppSyncClientRole-inspire',
    region: 'eu-west-1',
    featureConfigUrl:
      'https://gamma-eu-zaz.inspire-feature-config.cs.amazon.dev',
    featureConfigRegion: 'eu-south-2',
    sushiUrl: 'https://unagi-eu.amazon.com/1/events/com.amazon.csm.csa.prod',
    cscEntityPrefix: 'cscentral_eu_user',
    katalLoggerUrl: '', // disabled; see NOTE above
    cscDomain: 'https://cscentral-eu.amazon.com',
    regionName: 'eu',
    url: 'https://eu-gamma.inspire.cs.amazon.dev',
    rumConfig: {
      appConfig: {
        APPLICATION_ID: '088adf2b-ff2d-477c-810c-68b920a43fd4',
        APPLICATION_VERSION: '1.0.0',
        APPLICATION_REGION: 'eu-west-1',
      },
      runConfig: {
        ...DEFAULT_RUM_CONFIG,
        identityPoolId: 'eu-west-1:6f89738e-ece9-4700-8cdd-7d90f155f854',
        endpoint: 'https://dataplane.rum.eu-west-1.amazonaws.com',
      },
    },
  },
  gamma_fe: {
    graphQLEndpoint: 'https://fe-gamma.appsync.inspire.cs.amazon.dev/graphql',
    notificationGraphqlEndpoint:
      'https://o5vpgujn5zbxpdlbuqsiuahuye.appsync-api.us-west-2.amazonaws.com/graphql',
    notificationRegion: 'us-west-2',
    notificationSTSRoleArn:
      'arn:aws:iam::262366952739:role/AppSyncClientRole-inspire',
    region: 'us-west-2',
    featureConfigUrl: 'https://gamma-fe.inspire-feature-config.cs.amazon.dev',
    sushiUrl: 'https://unagi-fe.amazon.com/1/events/com.amazon.csm.csa.prod',
    cscEntityPrefix: 'cscentral_fe_user',
    katalLoggerUrl: '', // disabled; see NOTE above
    cscDomain: 'https://cscentral-fe.amazon.com',
    regionName: 'fe',
    url: 'https://fe-gamma.inspire.cs.amazon.dev',
    rumConfig: {
      appConfig: {
        APPLICATION_ID: '75550813-1fc3-49a9-957e-f0052d841a11',
        APPLICATION_VERSION: '1.0.0',
        APPLICATION_REGION: 'us-west-2',
      },
      runConfig: {
        ...DEFAULT_RUM_CONFIG,
        identityPoolId: 'us-west-2:fc08cecb-b2a6-45cf-894a-bd2d9b8abfc5',
        endpoint: 'https://dataplane.rum.us-west-2.amazonaws.com',
      },
    },
  },
  gamma_cn: {
    graphQLEndpoint:
      'https://ae24g7r4e5fhplxkyg4kcf7soq.appsync-api.cn-north-1.amazonaws.com.cn/graphql',
    region: 'cn-north-1',
    featureConfigUrl:
      'https://b0jxu5d90f.execute-api.cn-north-1.amazonaws.com.cn/live/',
    sushiUrl: 'https://unagi-cn.amazon.com/1/events/com.amazon.csm.csa.prod',
    cscEntityPrefix: 'cscentral_cn_user',
    katalLoggerUrl: '', // disabled; see NOTE above
    cscDomain: 'https://cscentral-cn.amazon.com',
    regionName: 'cn',
    url: 'https://cn-gamma.inspire.a2z.org.cn',
  },
  prod_na: {
    graphQLEndpoint: 'https://na.appsync.inspire.cs.amazon.dev/graphql',
    notificationGraphqlEndpoint:
      'https://iztn7enwavhq3dwwsvfuondcye.appsync-api.us-east-1.amazonaws.com/graphql',
    notificationRegion: 'us-east-1',
    notificationSTSRoleArn:
      'arn:aws:iam::123459884830:role/AppSyncClientRole-inspire',
    region: 'us-east-1',
    featureConfigUrl: 'https://prod-na.inspire-feature-config.cs.amazon.dev',
    sushiUrl: 'https://unagi-na.amazon.com/1/events/com.amazon.csm.csa.prod',
    cscEntityPrefix: 'cscentral_na_user',
    katalLoggerUrl: '', // disabled; see NOTE above
    cscDomain: 'https://cscentral.amazon.com',
    regionName: 'na',
    url: 'https://na.inspire.cs.amazon.dev',
    rumConfig: {
      appConfig: {
        APPLICATION_ID: '2221bc78-ae60-48cf-898f-5974cef4d541',
        APPLICATION_VERSION: '1.0.0',
        APPLICATION_REGION: 'us-east-1',
      },
      runConfig: {
        ...DEFAULT_RUM_CONFIG,
        identityPoolId: 'us-east-1:3b60258e-49af-406b-a6eb-edf338a5c773',
        endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
      },
    },
  },
  prod_eu: {
    graphQLEndpoint: 'https://eu.appsync.inspire.cs.amazon.dev/graphql',
    notificationGraphqlEndpoint:
      'https://zv3gyqircredpf43wscbyalbx4.appsync-api.eu-west-1.amazonaws.com/graphql',
    notificationRegion: 'eu-west-1',
    notificationSTSRoleArn:
      'arn:aws:iam::494594769015:role/AppSyncClientRole-inspire',
    region: 'eu-west-1',
    featureConfigUrl:
      'https://prod-eu-zaz.inspire-feature-config.cs.amazon.dev',
    featureConfigRegion: 'eu-south-2',
    sushiUrl: 'https://unagi-eu.amazon.com/1/events/com.amazon.csm.csa.prod',
    cscEntityPrefix: 'cscentral_eu_user',
    katalLoggerUrl: '', // disabled; see NOTE above
    cscDomain: 'https://cscentral-eu.amazon.com',
    regionName: 'eu',
    url: 'https://eu.inspire.cs.amazon.dev',
    rumConfig: {
      appConfig: {
        APPLICATION_ID: '9bf03b9f-2b8d-4966-867e-dbb75a5e9709',
        APPLICATION_VERSION: '1.0.0',
        APPLICATION_REGION: 'eu-west-1',
      },
      runConfig: {
        ...DEFAULT_RUM_CONFIG,
        identityPoolId: 'eu-west-1:111175cb-427a-4c1f-84da-6c95474f03bd',
        endpoint: 'https://dataplane.rum.eu-west-1.amazonaws.com',
      },
    },
  },
  prod_fe: {
    graphQLEndpoint: 'https://fe.appsync.inspire.cs.amazon.dev/graphql',
    notificationGraphqlEndpoint:
      'https://t43dnvr2xbd7vdsp7t5wk2zjka.appsync-api.us-west-2.amazonaws.com/graphql',
    notificationRegion: 'us-west-2',
    notificationSTSRoleArn:
      'arn:aws:iam::587223880017:role/AppSyncClientRole-inspire',
    region: 'us-west-2',
    featureConfigUrl: 'https://prod-fe.inspire-feature-config.cs.amazon.dev',
    sushiUrl: 'https://unagi-fe.amazon.com/1/events/com.amazon.csm.csa.prod',
    cscEntityPrefix: 'cscentral_fe_user',
    katalLoggerUrl: '', // disabled; see NOTE above
    cscDomain: 'https://cscentral-fe.amazon.com',
    regionName: 'fe',
    url: 'https://fe.inspire.cs.amazon.dev',
    rumConfig: {
      appConfig: {
        APPLICATION_ID: '246f6bcc-51da-4f9a-b99d-dc959004728f',
        APPLICATION_VERSION: '1.0.0',
        APPLICATION_REGION: 'us-west-2',
      },
      runConfig: {
        ...DEFAULT_RUM_CONFIG,
        identityPoolId: 'us-west-2:643a3478-cc76-45c3-87d9-98516f312514',
        endpoint: 'https://dataplane.rum.us-west-2.amazonaws.com',
      },
    },
  },
  prod_cn: {
    graphQLEndpoint:
      'https://hbmvciejtnhoxaa3dxoyubjq3u.appsync-api.cn-north-1.amazonaws.com.cn/graphql',
    region: 'cn-north-1',
    featureConfigUrl:
      'https://0v9rupaxrb.execute-api.cn-north-1.amazonaws.com.cn/live/',
    sushiUrl: 'https://unagi-cn.amazon.com/1/events/com.amazon.csm.csa.prod',
    cscEntityPrefix: 'cscentral_cn_user',
    katalLoggerUrl: '', // disabled; see NOTE above
    cscDomain: 'https://cscentral-cn.amazon.com',
    regionName: 'cn',
    url: 'https://cn.inspire.a2z.org.cn',
  },
};
